import { ArrowBack } from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";
import { addToCartAction } from "../../../../redux/cart/cart.reducers";
import { CartReduxService } from "../../../../redux/cart/CartReduxService";
import * as ProductActions from "../../../../redux/products/products.actions";
import * as ProductReducers from "../../../../redux/products/products.reducers";
import {
  AppDispatch,
  RootState,
  useAppDispatch,
} from "../../../../redux/store";
import { primary, secondary } from "../../../../themes/theme";
import AddToCartButton from "../../../../utils/common/addToCart-button/AddToCartButton";
import LayoutHeading from "../../../layout/components/layout-heading/LayoutHeading";
import MainNavBar from "../../../layout/pages/navbar/MainNavBar";
import SpinnerUI from "../../../ui/components/spinner/SpinnerUI";
import { IProductResponseView } from "../../models/IProductResponseView";

function ProductDetailsText(props: any) {
  return (
    <Grid container spacing={1} sx={{ mb: 1, pb: 1 }}>
      <Grid item xs={12} md={3}>
        <Typography sx={{ fontFamily: "Ubuntu", fontWeight: "450" }}>
          {props.primary}
        </Typography>
      </Grid>
      <Grid item xs={12} md={9}>
        <Typography
          sx={{
            fontWeight: props.primary === "Description" ? "400" : "bold",
            fontFamily: "Ubuntu",
          }}
        >
          {" : "}
          {props.secondary}
        </Typography>
      </Grid>
    </Grid>
  );
}

const ViewProduct: React.FC = () => {
  const { categoryName, productId } = useParams();
  const dispatch: AppDispatch = useAppDispatch();

  // Get Product Information from redux
  const productReduxState: ProductReducers.InitialState = useSelector(
    (state: RootState) => state[ProductReducers.productFeatureKey]
  );

  const { loading, product } = productReduxState;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    dispatch(ProductActions.getProductAction({ productId: productId }));
  }, [productId]);

  const clickAddToCart = (product: IProductResponseView) => {
    dispatch({
      type: `${addToCartAction}`,
      payload: {
        product: CartReduxService.convertToProductEntity(product),
        count: 1,
      },
    });
  };

  const handleMouseEnter = () => {
    setDialogOpen(true);
  };

  const handleMouseLeave = () => {
    setDialogOpen(false);
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    setZoomPosition({ x, y });
  };

  return (
    <>
      <Box sx={{ mb: 2, pb: 1 }}>
        {loading && <SpinnerUI />}
        <MainNavBar />
        <LayoutHeading
          heading="View Products"
          description="Explore detailed product information, including images, descriptions, and specifications."
        />
        {product && Object.keys(product).length > 0 && (
          <Grid
            container
            sx={{ pl: { xs: 1, sm: 1, md: 10, lg: 10 }, pr: 1, gap: 1 }}
          >
            <Grid item xs={11} md={11}>
              <Link
                to={`/products/${categoryName?.toLowerCase()}`}
                style={{
                  textDecoration: "none",
                  color: primary[900],
                  fontFamily: "Ubuntu",
                  borderRadius: "8px",
                  margin: 0,
                }}
              >
                <ArrowBack />
              </Link>
            </Grid>
            <Grid
              component={Paper}
              elevation={0}
              item
              xs={12}
              md={5}
              lg={4}
              sx={{
                borderRadius: "10px",
                background: secondary[50],
                height: "100%",
              }}
            >
              <Card elevation={0}>
                <Box>
                  <Typography
                    sx={{
                      fontFamily: "Ubuntu",
                      pt: 2,
                      fontSize: "17px",
                      letterSpacing: "1px",
                      pl: 2,
                      pb: 0,
                      mb: 0,
                      fontWeight: "bold",
                    }}
                  >
                    | Product Preview
                  </Typography>
                </Box>
                <Divider
                  sx={{
                    mt: 1,
                    mb: 0,
                    color: primary[900],
                    border: "1px dashed #f1f2f3",
                  }}
                />
                <CardContent sx={{}}>
                  <CardMedia
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      "& .img": {
                        height: "444px",
                        borderRadius: "10px",
                        border: "2px dashed transparent",
                        transition: "border 0.3s ease, outline 0.3s ease",
                        cursor: "crosshair",
                        "&:hover": {
                          border: "2px dashed lightgrey",
                        },
                      },
                    }}
                  >
                    <img
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      onMouseMove={handleMouseMove}
                      src={product.imageUrl}
                      className="img"
                    />
                  </CardMedia>
                </CardContent>
              </Card>
            </Grid>

            {dialogOpen ? (
              <Grid
                component={Paper}
                elevation={0}
                item
                xs={12}
                md={6}
                lg={7}
                sx={{ borderRadius: "10px", background: "transparent" }}
              >
                <Card elevation={0}>
                  <CardContent sx={{ mx: 1 }}>
                    <Box
                      sx={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={product?.imageUrl}
                        alt="Zoomed"
                        style={{
                          width: "auto",
                          height: "500px",
                          transformOrigin: `${zoomPosition.x}% ${zoomPosition.y}%`,
                          transform: "scale(3.5)",
                          borderRadius: "10px",
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              <Grid
                component={Paper}
                elevation={0}
                item
                xs={12}
                md={6}
                lg={7}
                sx={{ borderRadius: "10px", background: secondary[50] }}
              >
                <Card elevation={0}>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "Ubuntu",
                        pt: 2,
                        fontSize: "17px",
                        letterSpacing: "1px",
                        pl: 2,
                        pb: 0,
                        mb: 0,
                        fontWeight: "bold",
                      }}
                    >
                      | Product Details
                    </Typography>
                  </Box>
                  <Divider
                    sx={{
                      mt: 1,
                      mb: 0,
                      color: primary[900],
                      border: "1px dashed #f1f2f3",
                    }}
                  />
                  <CardContent sx={{ mx: 1 }}>
                    <ProductDetailsText
                      primary={"Name"}
                      secondary={product.title}
                    />
                    <ProductDetailsText
                      primary={"Brand"}
                      secondary={product.brand}
                    />
                    <ProductDetailsText
                      primary={"Category"}
                      secondary={product.categoryObj?.name}
                    />
                    <ProductDetailsText
                      primary={"Sub Category"}
                      secondary={product.subCategoryObj?.name}
                    />
                    <ProductDetailsText
                      primary={"Price"}
                      secondary={`₹${product.price?.toFixed(2)}`}
                    />
                    <ProductDetailsText
                      primary={"Description"}
                      secondary={product.description}
                    />
                    <Divider sx={{ my: 2, mt: 4, ml: "5px" }} />
                    <Box
                      sx={{
                        py: 1,
                        my: 2,
                        mt: 3,
                        width: "100%",
                        mx: "auto",
                        display: "flex",
                        justifyContent: { xs: "center", md: "flex-start" },
                      }}
                    >
                      <AddToCartButton
                        buttonText="Add To Cart"
                        onClick={() => clickAddToCart(product)}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        )}
      </Box>
    </>
  );
};

export default ViewProduct;
